import { Fragment } from "react";
import type { StrapiBlock } from "../types/cms";
import { mediaUrl } from "../lib/strapi";

type LeafChild = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
};

function renderLeaf(node: LeafChild, key: number) {
  let el: React.ReactNode = node.text;
  if (node.code) el = <code className="rounded bg-brand-paper px-1 py-0.5 text-sm">{el}</code>;
  if (node.bold) el = <strong>{el}</strong>;
  if (node.italic) el = <em>{el}</em>;
  if (node.underline) el = <u>{el}</u>;
  if (node.strikethrough) el = <s>{el}</s>;
  return <Fragment key={key}>{el}</Fragment>;
}

/**
 * Renderiza un nodo inline (text leaf o link) recursivamente.
 *
 * Strapi v5 representa los enlaces como nodos `{ type: "link", url, children: [...] }`
 * sin propiedad `text` directa, así que el chequeo `typeof child.text === "string"`
 * los descartaba silenciosamente. Aquí tratamos `link` antes que `text`.
 *
 * Externos (http/https) abren en pestaña nueva con `noopener noreferrer`.
 * Internos (rutas relativas, mailto:, tel:) se abren en la misma pestaña.
 */
function renderInline(node: any, key: number): React.ReactNode {
  if (node?.type === "link" && typeof node.url === "string") {
    const isExternal = /^https?:\/\//i.test(node.url);
    return (
      <a
        key={key}
        href={node.url}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        className="font-medium text-brand-orange-deep underline underline-offset-4 hover:text-brand-orange focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
      >
        {(node.children ?? []).map((c: any, i: number) => renderInline(c, i))}
      </a>
    );
  }
  if (typeof node?.text === "string") {
    return renderLeaf(node as LeafChild, key);
  }
  return null;
}

function renderChildren(children: any[]) {
  return (children ?? []).map((child, i) => renderInline(child, i));
}

export function BlocksRenderer({ blocks, className }: { blocks?: StrapiBlock[] | null; className?: string }) {
  if (!blocks || blocks.length === 0) return null;

  return (
    <div className={className ?? "prose-emjc max-w-prose space-y-4 text-lg leading-relaxed text-gray-700"}>
      {blocks.map((block, idx) => {
        switch (block.type) {
          case "paragraph":
            return <p key={idx}>{renderChildren(block.children)}</p>;
          case "heading": {
            const Tag = (`h${block.level}` as unknown) as keyof JSX.IntrinsicElements;
            return (
              <Tag key={idx} className="font-display font-semibold">
                {renderChildren(block.children)}
              </Tag>
            );
          }
          case "list":
            if (block.format === "ordered") {
              return (
                <ol key={idx} className="list-decimal pl-6">
                  {block.children.map((li: any, i: number) => (
                    <li key={i}>{renderChildren(li.children)}</li>
                  ))}
                </ol>
              );
            }
            return (
              <ul key={idx} className="list-disc pl-6">
                {block.children.map((li: any, i: number) => (
                  <li key={i}>{renderChildren(li.children)}</li>
                ))}
              </ul>
            );
          case "quote":
            return (
              <blockquote key={idx} className="border-l-4 border-brand-orange pl-4 italic">
                {renderChildren(block.children)}
              </blockquote>
            );
          case "code":
            return (
              <pre key={idx} className="overflow-x-auto rounded-lg bg-brand-ink p-4 text-sm text-brand-paper">
                <code>{block.children.map((c: any) => c.text).join("\n")}</code>
              </pre>
            );
          case "image":
            return (
              <figure key={idx} className="my-6">
                <img
                  src={mediaUrl(block.image.url)}
                  alt={block.image.alternativeText ?? ""}
                  className="w-full rounded-2xl"
                  loading="lazy"
                />
                {block.image.alternativeText && (
                  <figcaption className="mt-2 text-sm text-gray-600">{block.image.alternativeText}</figcaption>
                )}
              </figure>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
